/**
 * Catalog Browse Page
 * Search and filter building materials / products catalog
 */

import {
	PRODUCT_ITEM_KIND_LABELS,
	PRODUCT_ITEM_KINDS,
	type ProductItemKind,
} from "@shared/types";
import {
	ArrowUpDown,
	FolderPlus,
	Layers,
	Search,
	SlidersHorizontal,
	Star,
	X,
} from "lucide-react";
import {
	type FormEvent,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleChip } from "@/components/ui/toggle-chip";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { CategoryTreeSelect } from "@/features/catalog/components/category-tree-select";
import {
	addCollectionItem,
	addFavorite,
	type CatalogBrand,
	type CatalogCategoryTreeNode,
	type DesignerCatalogCollection,
	type DesignerCatalogFavorite,
	getBrands,
	getCategoryTree,
	listCollections,
	listFavorites,
	type ProductSearchResult,
	removeFavorite,
	searchCatalogProducts,
} from "@/features/catalog/services/catalog-api.service";
import { indexCategoryTree } from "@/features/catalog/utils/category-tree";
import {
	PRICE_ON_REQUEST,
	searchResultPriceLabel,
} from "@/features/catalog/utils/price-label";
import { formatUom } from "@/features/catalog/utils/uom";
import { Chip, FloatPanel, StudioButton } from "@/features/studio/theme";
import {
	CollectionsTab,
	CustomProductsTab,
	FavoritesTab,
} from "./designer-catalog-tabs";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const QUALITY_TIERS = ["standard", "premium", "luxury"] as const;

// The dropdown omits composite rows (supply + install): they have no consumer
// pricing model yet, matching the BOQ picker in add-entry-dialog. The API does
// not enforce this; a hand-written `?itemKind=composite` is reset to Materials
// by the page, see `selectedItemKind`.
const ITEM_KINDS = PRODUCT_ITEM_KINDS.filter(
	(k): k is Exclude<ProductItemKind, "composite"> => k !== "composite",
);
const ITEM_KIND_LABELS = PRODUCT_ITEM_KIND_LABELS;

const SORT_OPTIONS = [
	{ value: "", label: "Relevance" },
	{ value: "price_asc", label: "Price: Low to High" },
	{ value: "price_desc", label: "Price: High to Low" },
	{ value: "newest", label: "Newest" },
];

const COMPARE_STORAGE_KEY = "catalog_compare_ids";
const MAX_COMPARE = 4;

/**
 * What the URL's `categoryId` turned out to be once checked against the tree.
 *
 * - `none`: no `categoryId` in the URL.
 * - `leaf`: a category products are actually filed on. The only one that filters.
 * - `branch`: a real category with children. Nothing is filed on it, so
 *   searching on it would return an empty grid; the picker drills into it
 *   instead.
 * - `resolving`: the tree has not arrived -- still in flight, or the fetch
 *   failed -- so the id cannot be classified. Treated as a leaf on purpose --
 *   see `categoryFilter` below.
 * - `unknown`: the tree has arrived *successfully* and does not contain the id
 *   at all. A deleted category, a stale bookmark or a typo in a shared link.
 *   Never reached on a failed fetch, which proves nothing about the id.
 */
type CategoryFilterStatus =
	| "none"
	| "leaf"
	| "branch"
	| "resolving"
	| "unknown";

interface CategoryFilterState {
	status: CategoryFilterStatus;
	/** The id to search on, or "" when the filter must not be applied. */
	searchId: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getCompareIds(): string[] {
	try {
		const stored = localStorage.getItem(COMPARE_STORAGE_KEY);
		return stored ? JSON.parse(stored) : [];
	} catch {
		return [];
	}
}

function setCompareIds(ids: string[]) {
	localStorage.setItem(COMPARE_STORAGE_KEY, JSON.stringify(ids));
}

// ---------------------------------------------------------------------------
// Product Card
// ---------------------------------------------------------------------------

function ProductCard({
	product,
	compareIds,
	onToggleCompare,
	isFavorited,
	onToggleFavorite,
	collections,
	onAddToCollection,
}: {
	product: ProductSearchResult["products"][number];
	compareIds: string[];
	onToggleCompare: (id: string) => void;
	isFavorited: boolean;
	onToggleFavorite: (id: string, e: React.MouseEvent) => void;
	collections: DesignerCatalogCollection[];
	onAddToCollection: (collectionId: string, productId: string) => void;
}) {
	const navigate = useNavigate();
	const { user } = useAuthStore();
	const isDesigner = user?.userType === "designer";
	const isCompared = compareIds.includes(product.id);
	const uomLabel = formatUom(product.uom);
	const priceLabel = searchResultPriceLabel(product);
	const [showCollectionList, setShowCollectionList] = useState(false);

	return (
		<div style={{ position: "relative" }}>
			<button
				type="button"
				onClick={() => navigate(`/catalog/${product.id}`)}
				style={{
					cursor: "pointer",
					width: "100%",
					textAlign: "left",
					background: "transparent",
					border: "none",
					padding: 0,
					display: "block",
				}}
			>
				<FloatPanel elevation={1} padding={0} style={{ overflow: "hidden" }}>
					{/* Image */}
					<div
						style={{
							aspectRatio: "4/3",
							width: "100%",
							background: "var(--color-dashboard-divider)",
							position: "relative",
						}}
					>
						{product.primaryImage ? (
							<img
								src={product.primaryImage}
								alt={product.name}
								style={{
									width: "100%",
									height: "100%",
									objectFit: "cover",
									filter: "saturate(0.92) contrast(1.06)",
								}}
							/>
						) : (
							<div
								style={{
									width: "100%",
									height: "100%",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<Layers
									className="h-10 w-10"
									style={{ color: "var(--color-dashboard-muted)" }}
								/>
							</div>
						)}

						{/* Favorite toggle */}
						{isDesigner && (
							<button
								type="button"
								onClick={(e) => onToggleFavorite(product.id, e)}
								style={{
									position: "absolute",
									left: 8,
									top: 8,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									borderRadius: 6,
									border: "1px solid var(--color-dashboard-border)",
									width: 32,
									height: 32,
									background: "var(--color-dashboard-paper)",
									color: isFavorited
										? "var(--color-dashboard-pill-awaiting)"
										: "var(--color-dashboard-muted)",
									cursor: "pointer",
									transition: "all 150ms ease",
								}}
								title={
									isFavorited ? "Remove from Favorites" : "Add to Favorites"
								}
							>
								<Star
									className="h-4 w-4"
									fill={isFavorited ? "#A16207" : "none"}
								/>
							</button>
						)}

						{/* Compare toggle */}
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								onToggleCompare(product.id);
							}}
							style={{
								position: "absolute",
								right: 8,
								top: 8,
								display: "flex",
								alignItems: "center",
								gap: 4,
								borderRadius: 6,
								border: isCompared
									? "1.5px solid var(--color-dashboard-accent)"
									: "1.5px solid var(--color-border-default)",
								padding: "4px 8px",
								fontSize: 11,
								fontWeight: 600,
								fontFamily: "var(--font-mono)",
								letterSpacing: "0.05em",
								textTransform: "uppercase",
								background: isCompared
									? "var(--color-dashboard-accent)"
									: "var(--color-dashboard-paper)",
								color: isCompared
									? "var(--color-dashboard-on-fill)"
									: "var(--color-dashboard-text)",
								cursor: "pointer",
								transition: "all 150ms ease",
							}}
						>
							{isCompared ? "Added" : "Compare"}
						</button>

						{/* Add to Collection toggle */}
						{isDesigner && (
							<button
								type="button"
								onClick={(e) => {
									e.stopPropagation();
									setShowCollectionList(!showCollectionList);
								}}
								style={{
									position: "absolute",
									right: 8,
									bottom: 8,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									borderRadius: 6,
									border: "1px solid var(--color-dashboard-border)",
									width: 32,
									height: 32,
									background: "var(--color-dashboard-paper)",
									color: "var(--color-dashboard-muted)",
									cursor: "pointer",
									transition: "all 150ms ease",
								}}
								title="Add to Collection"
							>
								<FolderPlus className="h-4 w-4" />
							</button>
						)}
					</div>

					{/* Body */}
					<div style={{ padding: 16 }}>
						{/* Name */}
						<h3
							style={{
								fontFamily: "var(--font-display)",
								fontWeight: 600,
								fontSize: 15,
								color: "var(--color-dashboard-text)",
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
							}}
						>
							{product.name}
						</h3>

						{/* Brand */}
						<p
							style={{
								display: "flex",
								fontFamily: "var(--font-body)",
								fontSize: 12.5,
								color: "var(--color-dashboard-muted)",
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
								marginTop: 2,
							}}
						>
							{product.brandName || product.categoryName || "—"}
						</p>

						{/* Tier badge */}
						<span
							style={{
								display: "inline-block",
								borderRadius: 9999,
								padding: "2px 8px",
								fontSize: 10,
								fontWeight: 600,
								textTransform: "uppercase",
								fontFamily: "var(--font-mono)",
								letterSpacing: "0.08em",
								marginTop: 8,
								background:
									product.tier === "luxury"
										? "var(--color-dashboard-pill-awaiting-bg)"
										: product.tier === "premium"
											? "var(--color-dashboard-pill-info-bg)"
											: "var(--color-dashboard-divider)",
								color:
									product.tier === "luxury"
										? "var(--color-dashboard-warning)"
										: product.tier === "premium"
											? "var(--color-dashboard-info-text)"
											: "var(--color-dashboard-muted-dark)",
							}}
						>
							{product.tier}
						</span>

						{/* Price */}
						<div
							data-testid="catalog-card-price"
							style={{
								display: "flex",
								alignItems: "baseline",
								gap: 4,
								marginTop: 6,
							}}
						>
							{priceLabel === null ? (
								<span
									style={{
										fontSize: 13,
										color: "var(--color-dashboard-muted)",
										fontFamily: "var(--font-body)",
									}}
								>
									{PRICE_ON_REQUEST}
								</span>
							) : (
								<>
									<span
										style={{
											fontFamily: "var(--font-display)",
											fontWeight: 500,
											fontSize: 17,
											color: "var(--color-dashboard-text)",
										}}
									>
										{priceLabel}
									</span>
									<span
										style={{
											fontFamily: "var(--font-mono)",
											fontSize: 10,
											color: "var(--color-dashboard-muted)",
										}}
									>
										/{uomLabel}
									</span>
								</>
							)}
						</div>
					</div>
				</FloatPanel>
			</button>

			{/* Collection Selection Dropdown */}
			{showCollectionList && (
				<FloatPanel
					elevation={3}
					padding={12}
					style={{
						position: "absolute",
						bottom: 48,
						right: 8,
						zIndex: 50,
						width: 200,
						maxHeight: 180,
						overflowY: "auto",
						background: "var(--color-dashboard-paper)",
						border: "1px solid var(--color-dashboard-border)",
					}}
				>
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							marginBottom: 8,
							paddingBottom: 4,
							borderBottom: "1px solid var(--color-dashboard-border-soft)",
						}}
					>
						<span
							style={{
								fontSize: 11,
								fontWeight: 600,
								color: "var(--color-dashboard-muted)",
							}}
						>
							ADD TO COLLECTION
						</span>
						<button
							type="button"
							onClick={() => setShowCollectionList(false)}
							style={{
								border: "none",
								background: "none",
								cursor: "pointer",
								color: "var(--color-dashboard-muted)",
							}}
						>
							<X className="h-3 w-3" />
						</button>
					</div>
					{collections.length === 0 ? (
						<p
							style={{
								fontSize: 12,
								color: "var(--color-dashboard-muted)",
								padding: "4px 0",
							}}
						>
							No collections created yet.
						</p>
					) : (
						<div className="space-y-1">
							{collections.map((col) => (
								<button
									key={col.id}
									type="button"
									onClick={() => {
										onAddToCollection(col.id, product.id);
										setShowCollectionList(false);
									}}
									style={{
										width: "100%",
										textAlign: "left",
										padding: "6px 8px",
										fontSize: 12.5,
										borderRadius: 4,
										color: "var(--color-dashboard-text)",
										background: "transparent",
										border: "none",
										cursor: "pointer",
									}}
									className="hover:bg-dashboard-warm-350"
								>
									{col.name}
								</button>
							))}
						</div>
					)}
				</FloatPanel>
			)}
		</div>
	);
}

// Comparison Tray removed - moved to sidebar panel

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export function CatalogBrowsePage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const { user } = useAuthStore();
	const isDesigner = user?.userType === "designer";

	const parseCsvParam = (key: string): string[] => {
		const value = searchParams.get(key);
		return value ? value.split(",").filter(Boolean) : [];
	};

	// Active tab
	const [activeTab, setActiveTab] = useState<
		"all" | "favorites" | "collections" | "custom"
	>(() => {
		const tab = searchParams.get("tab");
		if (
			tab === "all" ||
			tab === "favorites" ||
			tab === "collections" ||
			tab === "custom"
		) {
			return tab;
		}
		return "all";
	});

	useEffect(() => {
		const tab = searchParams.get("tab");
		if (
			tab === "all" ||
			tab === "favorites" ||
			tab === "collections" ||
			tab === "custom"
		) {
			setActiveTab(tab);
		}
	}, [searchParams]);

	const handleTabChange = (
		tab: "all" | "favorites" | "collections" | "custom",
	) => {
		setActiveTab(tab);
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			next.set("tab", tab);
			return next;
		});
	};

	// Phase 2 State
	const [favorites, setFavorites] = useState<DesignerCatalogFavorite[]>([]);
	const [collections, setCollections] = useState<DesignerCatalogCollection[]>(
		[],
	);

	// Reference data
	// `null` until a fetch has *succeeded*, so an empty taxonomy (`[]`) stays
	// distinguishable from one that never arrived: the two mean opposite things
	// for an id the tree does not contain. A failed fetch leaves this null rather
	// than emptying it, which is the difference between "this category is gone"
	// and "we could not check".
	const [categoryTree, setCategoryTree] = useState<
		CatalogCategoryTreeNode[] | null
	>(null);
	const isCategoryTreeLoaded = categoryTree !== null;
	const [brands, setBrands] = useState<CatalogBrand[]>([]);

	// Results
	const [results, setResults] = useState<ProductSearchResult>({
		products: [],
		total: 0,
		page: 1,
		totalPages: 0,
	});
	const [isLoading, setIsLoading] = useState(true);

	// Compare
	const [compareIds, setCompareIdsState] = useState<string[]>(getCompareIds);

	// Sync compare state with product-detail page
	useEffect(() => {
		function handleCompareChange(e: Event): void {
			const customEvent = e as CustomEvent<string[]>;
			setCompareIdsState(customEvent.detail);
		}
		window.addEventListener("catalog-compare-changed", handleCompareChange);
		return () =>
			window.removeEventListener(
				"catalog-compare-changed",
				handleCompareChange,
			);
	}, []);

	// ---------------------------------------------------------------------------
	// Read filters from URL search params
	// ---------------------------------------------------------------------------

	const q = searchParams.get("q") || "";
	const categoryId = searchParams.get("categoryId") || "";
	const selectedBrands = parseCsvParam("brands");
	const selectedTiers = parseCsvParam("tiers");
	const rawItemKind = searchParams.get("itemKind");
	const selectedItemKind =
		rawItemKind === "all" ||
		(rawItemKind && (ITEM_KINDS as readonly string[]).includes(rawItemKind))
			? rawItemKind
			: "material";
	const itemKindNotApplied = Boolean(
		rawItemKind && rawItemKind !== selectedItemKind,
	);
	const priceMinRupees = searchParams.get("priceMin") || "";
	const priceMaxRupees = searchParams.get("priceMax") || "";
	const sortBy = searchParams.get("sortBy") || "";
	const page = parseInt(searchParams.get("page") || "1", 10);

	// ---------------------------------------------------------------------------
	// Helper to update a single param (keeps other params intact)
	// ---------------------------------------------------------------------------

	const setParam = useCallback(
		(key: string, value: string) => {
			setSearchParams((prev) => {
				const next = new URLSearchParams(prev);
				if (value) {
					next.set(key, value);
				} else {
					next.delete(key);
				}
				// Reset to page 1 when any filter changes (except page itself)
				if (key !== "page") {
					next.delete("page");
				}
				return next;
			});
		},
		[setSearchParams],
	);

	const categoryIndex = useMemo(
		() => indexCategoryTree(categoryTree ?? []),
		[categoryTree],
	);

	// Products are filed on leaf categories only, so searching by a branch id
	// matches nothing and shows an empty grid that reads as a bug rather than as
	// "this level holds no products". The picker never writes a branch id, but a
	// hand-edited or pre-plan-003 URL can still carry one, so drop it here and
	// let the picker's hint explain instead.
	//
	// An id the tree does not contain is only droppable once there is a tree to
	// check it against. Before that, every id is unknown, and dropping them would
	// fire a second, unfiltered search on every page load carrying a leaf filter
	// -- the common case -- so `resolving` passes the id straight through and the
	// first search is the right one. Once the tree has landed the same id means
	// something quite different: the category is gone. Searching on it would show
	// an empty grid under a picker displaying "All Categories", which is a lie in
	// both directions, so it is dropped and said out loud instead.
	//
	// A tree that failed to load stays `resolving` forever rather than becoming
	// "landed and empty". Otherwise a dropped connection would drop a perfectly
	// valid category filter and tell the user their link no longer exists, which
	// is both wrong and unrecoverable without editing the URL by hand.
	const categoryFilter = useMemo<CategoryFilterState>(() => {
		if (!categoryId) {
			return { status: "none", searchId: "" };
		}
		const entry = categoryIndex.get(categoryId);
		if (entry) {
			return entry.node.children.length > 0
				? { status: "branch", searchId: "" }
				: { status: "leaf", searchId: categoryId };
		}
		if (!isCategoryTreeLoaded) {
			return { status: "resolving", searchId: categoryId };
		}
		return { status: "unknown", searchId: "" };
	}, [categoryIndex, categoryId, isCategoryTreeLoaded]);

	const effectiveCategoryId = categoryFilter.searchId;

	const setArrayParam = useCallback(
		(key: string, values: string[]) => {
			setSearchParams((prev) => {
				const next = new URLSearchParams(prev);
				if (values.length > 0) {
					next.set(key, values.join(","));
				} else {
					next.delete(key);
				}
				next.delete("page");
				return next;
			});
		},
		[setSearchParams],
	);

	// ---------------------------------------------------------------------------
	// Load reference and Phase 2 data on mount
	// ---------------------------------------------------------------------------

	useEffect(() => {
		// `getCategoryTree` resolves to null when the request failed. Only a real
		// answer counts as loaded: leaving the tree null on failure keeps the URL's
		// categoryId in `resolving`, so it stays on the search instead of being
		// dropped and reported as a deleted category. The user sees the results
		// their link asked for; only the picker is missing.
		getCategoryTree().then((tree) => {
			if (tree) {
				setCategoryTree(tree);
			}
		});
		getBrands().then(setBrands);

		// Load favorites and collections only for designers
		if (isDesigner) {
			listFavorites().then(setFavorites);
			listCollections().then(setCollections);
		}
	}, [isDesigner]);

	// ---------------------------------------------------------------------------
	// Fetch products when filters change
	// ---------------------------------------------------------------------------

	const fetchProducts = useCallback(async () => {
		setIsLoading(true);
		try {
			const priceMinPaisa = priceMinRupees
				? parseInt(priceMinRupees, 10) * 100
				: undefined;
			const priceMaxPaisa = priceMaxRupees
				? parseInt(priceMaxRupees, 10) * 100
				: undefined;

			const data = await searchCatalogProducts({
				q: q || undefined,
				categoryId: effectiveCategoryId || undefined,
				brandId: selectedBrands.length > 0 ? selectedBrands : undefined,
				tier: selectedTiers.length > 0 ? selectedTiers : undefined,
				priceMin: priceMinPaisa,
				priceMax: priceMaxPaisa,
				sortBy: sortBy || undefined,
				page,
				perPage: 12,
				itemKind: selectedItemKind,
			});
			setResults(data);
		} catch {
			console.error("Failed to fetch catalog products");
		} finally {
			setIsLoading(false);
		}
	}, [
		q,
		effectiveCategoryId,
		priceMinRupees,
		priceMaxRupees,
		sortBy,
		page,
		selectedBrands.length,
		selectedTiers.length,
		selectedBrands[0],
		selectedTiers[0],
		selectedItemKind,
	]);

	useEffect(() => {
		fetchProducts();
	}, [fetchProducts]);

	// ---------------------------------------------------------------------------
	// Filter handlers
	// ---------------------------------------------------------------------------

	const handleSearch = (e: FormEvent) => {
		e.preventDefault();
	};

	const toggleBrand = (brandId: string) => {
		const next = selectedBrands.includes(brandId)
			? selectedBrands.filter((b) => b !== brandId)
			: [...selectedBrands, brandId];
		setArrayParam("brands", next);
	};

	const toggleTier = (tier: string) => {
		const next = selectedTiers.includes(tier)
			? selectedTiers.filter((t) => t !== tier)
			: [...selectedTiers, tier];
		setArrayParam("tiers", next);
	};

	const clearFilters = () => {
		setSearchParams(new URLSearchParams());
	};

	// Deliberately the id being searched on rather than the one in the URL: a
	// branch or an unknown id is not narrowing anything, so offering "Clear
	// filters" for it claims a filter that is not applied. An unknown id still
	// needs a way out of the URL, and gets its own removal control in the notice
	// below.
	const hasFilters =
		q ||
		effectiveCategoryId ||
		selectedBrands.length > 0 ||
		selectedTiers.length > 0 ||
		priceMinRupees ||
		priceMaxRupees ||
		sortBy ||
		selectedItemKind !== "material";

	// ---------------------------------------------------------------------------
	// Compare handlers
	// ---------------------------------------------------------------------------

	const toggleCompare = (productId: string) => {
		setCompareIdsState((prev) => {
			let next: string[];
			if (prev.includes(productId)) {
				next = prev.filter((id) => id !== productId);
			} else {
				if (prev.length >= MAX_COMPARE) return prev;
				next = [...prev, productId];
			}
			setCompareIds(next);
			return next;
		});
	};

	const clearCompare = () => {
		setCompareIdsState([]);
		setCompareIds([]);
	};

	// ---------------------------------------------------------------------------
	// Favorite & Collection Toggle handlers
	// ---------------------------------------------------------------------------

	async function handleToggleFavorite(productId: string, e: React.MouseEvent) {
		e.stopPropagation();
		const fav = favorites.find((f) => f.catalogProductId === productId);
		if (fav) {
			const success = await removeFavorite(productId);
			if (success) {
				setFavorites((prev) =>
					prev.filter((f) => f.catalogProductId !== productId),
				);
				toast.success("Removed from favorites");
			} else {
				toast.error("Failed to remove favorite");
			}
		} else {
			const res = await addFavorite(productId);
			if (res.success && res.id) {
				setFavorites((prev) => [
					...prev,
					{
						id: res.id,
						catalogProductId: productId,
					} as DesignerCatalogFavorite,
				]);
				toast.success("Added to favorites");
			} else {
				toast.error("Failed to add favorite");
			}
		}
	}

	async function handleAddToCollection(
		collectionId: string,
		productId: string,
	) {
		const res = await addCollectionItem(collectionId, productId);
		if (res.success) {
			toast.success("Added to collection");
		} else {
			toast.error(res.error || "Failed to add to collection");
		}
	}

	// ---------------------------------------------------------------------------
	// Render
	// ---------------------------------------------------------------------------

	return (
		<div className="min-h-screen bg-transparent p-4 sm:p-12">
			<div className="mx-auto max-w-7xl space-y-8">
				{/* Top Masthead Panel */}
				<FloatPanel elevation={1} padding={32}>
					<div>
						<p className="mb-2 inline-flex items-center gap-2 rounded-full bg-dashboard-warm-350 px-3 py-1 text-xs font-semibold text-[var(--color-dashboard-accent-dark)]">
							<SlidersHorizontal className="h-3.5 w-3.5" />
							PRODUCT CATALOG
						</p>
						<h1
							style={{
								fontFamily: "var(--font-display)",
								fontWeight: 500,
								fontSize: 36,
								color: "var(--color-dashboard-text)",
								letterSpacing: "-0.02em",
							}}
						>
							Discover building{" "}
							<span
								style={{
									fontFamily: "var(--font-display)",
									fontStyle: "italic",
									color: "var(--color-dashboard-accent-display)",
								}}
							>
								materials
							</span>
							.
						</h1>
						<p
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 14,
								color: "var(--color-dashboard-muted-dark)",
								marginTop: 8,
								maxWidth: 600,
							}}
						>
							Curate materials, finishes, and custom products for your active
							project BOQs.
						</p>
					</div>
				</FloatPanel>

				{/* Modular Tab Toggles */}
				<div
					style={{
						display: "flex",
						flexWrap: "wrap",
						gap: 12,
						borderBottom: "1px solid var(--color-dashboard-border)",
						paddingBottom: 16,
					}}
				>
					<Chip
						active={activeTab === "all"}
						onClick={() => handleTabChange("all")}
					>
						Browse Catalog
					</Chip>
					{isDesigner && (
						<>
							<Chip
								active={activeTab === "favorites"}
								onClick={() => handleTabChange("favorites")}
							>
								Favorites
							</Chip>
							<Chip
								active={activeTab === "collections"}
								onClick={() => handleTabChange("collections")}
							>
								Collections
							</Chip>
							<Chip
								active={activeTab === "custom"}
								onClick={() => handleTabChange("custom")}
							>
								Custom Products
							</Chip>
						</>
					)}
				</div>

				{/* Conditional Tab Rendering */}
				{activeTab === "favorites" && <FavoritesTab />}
				{activeTab === "collections" && <CollectionsTab />}
				{activeTab === "custom" && <CustomProductsTab />}

				{activeTab === "all" && (
					<div className="flex flex-col gap-8 lg:flex-row">
						{/* Filters Sidebar */}
						<div className="w-full lg:w-64 shrink-0 lg:sticky lg:top-6 lg:self-start space-y-4">
							{/* Comparison Panel */}
							{compareIds.length > 0 && (
								<FloatPanel elevation={1} padding={20}>
									<div className="flex items-center justify-between mb-3">
										<span
											style={{
												fontFamily: "var(--font-display)",
												fontWeight: 600,
												fontSize: 14,
												color: "var(--color-dashboard-text)",
											}}
										>
											Comparison ({compareIds.length})
										</span>
										<button
											type="button"
											onClick={clearCompare}
											className="text-xs font-semibold text-[var(--color-dashboard-muted)] hover:text-dashboard-text"
											style={{
												background: "transparent",
												border: 0,
												cursor: "pointer",
											}}
										>
											Clear
										</button>
									</div>
									<div className="flex gap-2 mb-4 overflow-x-auto py-1 scrollbar-thin">
										{(
											compareIds
												.map((id) => results.products.find((p) => p.id === id))
												.filter(Boolean) as ProductSearchResult["products"]
										).map((p) => (
											<div
												key={p.id}
												className="h-10 w-10 shrink-0 overflow-hidden rounded-lg border border-[var(--color-border-default)] bg-dashboard-warm-450 relative group"
												title={p.name}
											>
												{p.primaryImage ? (
													<img
														src={p.primaryImage}
														alt={p.name}
														className="h-full w-full object-cover"
													/>
												) : (
													<div className="flex h-full w-full items-center justify-center">
														<Layers className="h-4 w-4 text-muted-foreground/30" />
													</div>
												)}
												<button
													type="button"
													onClick={() => toggleCompare(p.id)}
													className="absolute inset-0 bg-dashboard-scrim/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity border-0 cursor-pointer"
												>
													<X className="h-3 w-3 text-dashboard-on-fill" />
												</button>
											</div>
										))}
									</div>
									<StudioButton
										variant="primary"
										className="w-full"
										disabled={compareIds.length < 2}
										onClick={() =>
											navigate(`/catalog/compare?ids=${compareIds.join(",")}`)
										}
										style={{
											background: "var(--color-dashboard-accent)",
											color: "var(--color-dashboard-on-fill)",
										}}
									>
										Compare Materials
									</StudioButton>
								</FloatPanel>
							)}

							{/* Filters Sidebar */}
							<FloatPanel elevation={1} padding={20}>
								<div className="flex items-center justify-between mb-5">
									<div className="flex items-center gap-2">
										<SlidersHorizontal className="h-4 w-4 text-[var(--color-dashboard-accent-dark)]" />
										<h2
											style={{
												fontFamily: "var(--font-display)",
												fontWeight: 600,
												fontSize: 15.5,
												color: "var(--color-dashboard-text)",
											}}
										>
											{t("common.filters")}
										</h2>
									</div>
									{hasFilters && (
										<button
											type="button"
											onClick={clearFilters}
											className="flex items-center gap-1 text-xs font-semibold text-[var(--color-dashboard-accent-dark)] hover:text-[var(--color-dashboard-accent-dark)]/80"
											style={{
												background: "transparent",
												border: 0,
												cursor: "pointer",
											}}
										>
											<X className="h-3 w-3" />
											{t("common.clearFilters", "Clear filters")}
										</button>
									)}
								</div>

								{/* Filter List */}
								<div className="space-y-6">
									{/* Search Input */}
									<div className="space-y-2">
										<span
											style={{
												display: "block",
												fontSize: 13,
												fontWeight: 500,
												color: "var(--color-dashboard-muted-dark)",
											}}
										>
											Search
										</span>
										<form onSubmit={handleSearch}>
											<div style={{ position: "relative" }}>
												<Search
													className="absolute left-4 top-1/2 h-4 w-4  -translate-y-1/2"
													style={{ color: "var(--color-dashboard-muted)" }}
												/>
												<input
													type="text"
													value={q}
													onChange={(e) => setParam("q", e.target.value)}
													placeholder="Search keywords..."
													style={{
														width: "100%",
														padding: "10px 14px 10px 38px",
														border: "1px solid var(--color-border-default)",
														borderRadius: 7,
														background: "var(--color-dashboard-paper)",
														fontSize: 14,
														color: "var(--color-dashboard-text)",
														fontFamily: "var(--font-body)",
													}}
												/>
											</div>
										</form>
									</div>

									{/* Item Kind */}
									<div className="space-y-2">
										<span
											style={{
												display: "block",
												fontSize: 13,
												fontWeight: 500,
												color: "var(--color-dashboard-muted-dark)",
												marginBottom: 6,
											}}
										>
											Item Kind
										</span>
										<Select
											value={selectedItemKind}
											onValueChange={(value) =>
												setParam("itemKind", value === "material" ? "" : value)
											}
										>
											<SelectTrigger
												aria-label="Item kind"
												className="w-full"
												style={{
													borderRadius: 7,
													borderColor: "var(--color-border-default)",
													background: "var(--color-dashboard-paper)",
												}}
											>
												<SelectValue placeholder="Item kind" />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="all">All types</SelectItem>
												{ITEM_KINDS.map((kind) => (
													<SelectItem key={kind} value={kind}>
														{ITEM_KIND_LABELS[kind]}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										{itemKindNotApplied && (
											<p
												role="status"
												style={{
													fontSize: 12,
													lineHeight: 1.4,
													marginTop: 6,
													color: "var(--color-dashboard-muted-dark)",
												}}
											>
												{t(
													"catalog.unknownItemKindFilter",
													"The item type in this link is not offered here, so these are materials.",
												)}
											</p>
										)}
									</div>

									{/* Category drill-down (L1 > L2 > L3) */}
									<CategoryTreeSelect
										tree={categoryTree ?? []}
										value={categoryId}
										onChange={(value) => setParam("categoryId", value)}
										noneLabel="All Categories"
									/>

									{/*
									 * The picker cannot say anything about an id it cannot
									 * resolve -- it just shows "All Categories" -- so without
									 * this the user reads the page as unfiltered while the URL
									 * still carries a category. Say what happened, and give the
									 * id a way out of the URL, since `hasFilters` no longer
									 * counts it towards "Clear filters".
									 */}
									{categoryFilter.status === "unknown" && (
										<div
											role="status"
											style={{
												borderRadius: 7,
												border: "1px solid var(--color-dashboard-border)",
												background: "var(--color-dashboard-warm-100)",
												padding: 12,
											}}
										>
											<p
												style={{
													fontSize: 12,
													lineHeight: 1.4,
													color: "var(--color-dashboard-muted-dark)",
												}}
											>
												{t(
													"catalog.unknownCategoryFilter",
													"The category in this link no longer exists, so it is not being applied. These are all products.",
												)}
											</p>
											<button
												type="button"
												onClick={() => setParam("categoryId", "")}
												style={{
													marginTop: 8,
													background: "transparent",
													border: 0,
													padding: 0,
													cursor: "pointer",
													fontSize: 12,
													fontWeight: 600,
													color: "var(--color-dashboard-accent-dark)",
												}}
											>
												{t(
													"catalog.removeUnknownCategoryFilter",
													"Remove it from the link",
												)}
											</button>
										</div>
									)}

									{/* Brand selection */}
									<div className="space-y-2">
										<span
											style={{
												display: "block",
												fontSize: 13,
												fontWeight: 500,
												color: "var(--color-dashboard-muted-dark)",
												marginBottom: 8,
											}}
										>
											{t("catalog.brands", "Brands")}
										</span>
										<div className="space-y-2 max-h-48 overflow-y-auto pr-1">
											{brands.map((brand) => (
												<button
													key={brand.id}
													type="button"
													onClick={() => toggleBrand(brand.id)}
													style={{
														display: "flex",
														alignItems: "center",
														gap: 8,
														fontSize: 13.5,
														color: "var(--color-dashboard-muted-dark)",
														width: "100%",
														textAlign: "left",
														cursor: "pointer",
														border: 0,
														background: "transparent",
													}}
												>
													<input
														type="checkbox"
														checked={selectedBrands.includes(brand.id)}
														readOnly
														className="rounded border-[var(--color-border-default)] text-[var(--color-dashboard-accent-dark)] focus:ring-[var(--color-dashboard-accent)]"
													/>
													<span>{brand.name}</span>
												</button>
											))}
										</div>
									</div>

									{/* Quality Tiers */}
									<div className="space-y-2">
										<span
											style={{
												display: "block",
												fontSize: 13,
												fontWeight: 500,
												color: "var(--color-dashboard-muted-dark)",
												marginBottom: 8,
											}}
										>
											{t("catalog.qualityTier", "Quality Tier")}
										</span>
										<div className="flex flex-wrap gap-2">
											{QUALITY_TIERS.map((tier) => (
												<ToggleChip
													key={tier}
													selected={selectedTiers.includes(tier)}
													onClick={() => toggleTier(tier)}
												>
													{tier}
												</ToggleChip>
											))}
										</div>
									</div>

									{/* Price Range */}
									<div className="space-y-2">
										<span
											style={{
												display: "block",
												fontSize: 13,
												fontWeight: 500,
												color: "var(--color-dashboard-muted-dark)",
												marginBottom: 8,
											}}
										>
											Price Range (₹)
										</span>
										<div className="flex items-center gap-2">
											<input
												type="number"
												placeholder="Min"
												value={priceMinRupees}
												onChange={(e) => setParam("priceMin", e.target.value)}
												style={{
													width: "100%",
													padding: "10px 12px",
													border: "1px solid var(--color-border-default)",
													borderRadius: 7,
													background: "var(--color-dashboard-paper)",
													fontSize: 14,
													color: "var(--color-dashboard-text)",
													outline: "none",
												}}
											/>
											<span
												style={{
													color: "var(--color-dashboard-muted)",
													fontSize: 13,
												}}
											>
												to
											</span>
											<input
												type="number"
												placeholder="Max"
												value={priceMaxRupees}
												onChange={(e) => setParam("priceMax", e.target.value)}
												style={{
													width: "100%",
													padding: "10px 12px",
													border: "1px solid var(--color-border-default)",
													borderRadius: 7,
													background: "var(--color-dashboard-paper)",
													fontSize: 14,
													color: "var(--color-dashboard-text)",
													outline: "none",
												}}
											/>
										</div>
									</div>

									{/* Sort */}
									<div>
										<span
											style={{
												display: "block",
												fontSize: 13,
												fontWeight: 500,
												color: "var(--color-dashboard-muted-dark)",
												marginBottom: 6,
											}}
										>
											<span className="flex items-center gap-1.5">
												<ArrowUpDown className="h-3.5 w-3.5" />
												Sort By
											</span>
										</span>
										<Select
											value={sortBy || "__relevance__"}
											onValueChange={(value) =>
												setParam(
													"sortBy",
													value === "__relevance__" ? "" : value,
												)
											}
										>
											<SelectTrigger
												className="w-full"
												style={{
													borderRadius: 7,
													borderColor: "var(--color-border-default)",
													background: "var(--color-dashboard-paper)",
												}}
											>
												<SelectValue placeholder="Relevance" />
											</SelectTrigger>
											<SelectContent>
												{SORT_OPTIONS.map((opt) => (
													<SelectItem
														key={opt.value || "__relevance__"}
														value={opt.value || "__relevance__"}
													>
														{opt.label}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
								</div>
							</FloatPanel>
						</div>

						{/* Results Grid */}
						<div className="flex-1 space-y-4">
							<div className="flex items-center justify-between">
								<p
									style={{
										fontFamily: "var(--font-mono)",
										fontSize: 9,
										textTransform: "uppercase",
										letterSpacing: "0.08em",
										color: "var(--color-dashboard-muted)",
									}}
								>
									{isLoading
										? t("common.loading")
										: `${results.total} product${results.total !== 1 ? "s" : ""} found`}
								</p>
							</div>

							{isLoading ? (
								<div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
									{[1, 2, 3, 4, 5, 6].map((i) => (
										<Skeleton key={i} className="h-72 rounded-lg" />
									))}
								</div>
							) : results.products.length === 0 ? (
								<FloatPanel elevation={1} padding={48}>
									<div style={{ textAlign: "center" }}>
										<p
											style={{
												fontFamily: "var(--font-body)",
												color: "var(--color-dashboard-muted)",
											}}
										>
											{t("common.noResults")}
										</p>
										{hasFilters && (
											<button
												type="button"
												onClick={clearFilters}
												style={{
													marginTop: 16,
													background: "transparent",
													border: 0,
													cursor: "pointer",
													fontFamily: "var(--font-body)",
													fontSize: 14,
													color: "var(--color-dashboard-accent-dark)",
													fontWeight: 600,
												}}
											>
												{t("common.clearFilters")}
											</button>
										)}
									</div>
								</FloatPanel>
							) : (
								<>
									<div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
										{results.products.map((product) => (
											<ProductCard
												key={product.id}
												product={product}
												compareIds={compareIds}
												onToggleCompare={toggleCompare}
												isFavorited={favorites.some(
													(f) => f.catalogProductId === product.id,
												)}
												onToggleFavorite={handleToggleFavorite}
												collections={collections}
												onAddToCollection={handleAddToCollection}
											/>
										))}
									</div>

									{/* Pagination */}
									{results.totalPages > 1 && (
										<div
											style={{
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												gap: 8,
												marginTop: 24,
											}}
										>
											<StudioButton
												type="button"
												variant="quiet"
												size="sm"
												onClick={() =>
													setParam("page", String(Math.max(1, page - 1)))
												}
												disabled={page === 1}
											>
												{t("common.previous")}
											</StudioButton>
											<span
												style={{
													fontFamily: "var(--font-body)",
													fontSize: 13,
													color: "var(--color-dashboard-muted)",
												}}
											>
												{t("common.pageOf", {
													page,
													totalPages: results.totalPages,
												})}
											</span>
											<StudioButton
												type="button"
												variant="quiet"
												size="sm"
												onClick={() =>
													setParam(
														"page",
														String(Math.min(results.totalPages, page + 1)),
													)
												}
												disabled={page === results.totalPages}
											>
												{t("common.next")}
											</StudioButton>
										</div>
									)}
								</>
							)}
						</div>
					</div>
				)}
			</div>

			{/* Comparison Tray removed */}
		</div>
	);
}
